import { ATS_INTERACTIVE_STAGES, type AtsInteractiveStage } from "../enums-ats";
import { type CollectionTiming, readCollectionTiming } from "./hiring-document-timing";
import { JOB_STATUSES, type JobStatus } from "./hiring";

/* A posting as the web's job builder and the phone hold it: the job's own fields, and beside them
   the documents, written once the job has an id. */

export interface JobValues {
  facilityId: string;
  boardId: string | null;
  title: string;
  slug: string | null;
  department: string | null;
  location: string | null;
  employmentType: string | null;
  salaryRange: string | null;
  description: string;
  responsibilities: string | null;
  requirements: string | null;
  interactiveStages: AtsInteractiveStage[];
  skipOfferLetter: boolean;
  skipAcceptanceInfo: boolean;
  acceptanceTemplateId: string | null;
  allowSignaturesLater: boolean;
  defaultPayType: string | null;
  defaultPayRate: string | null;
  videoInstructions: string | null;
  status: JobStatus;
}

export interface JobDocumentDraft {
  documentTypeId: string | null;
  label: string;
  isRequired: boolean;
  collectionTiming: CollectionTiming;
}

export const JOB_BUILDER_STEPS = [
  { value: "posting", label: "The posting" },
  { value: "screening", label: "Screening" },
  { value: "application", label: "Application form" },
  { value: "stages", label: "Extra stages" },
  { value: "offer", label: "The offer" },
  { value: "paperwork", label: "Paperwork" },
  { value: "review", label: "Review" },
] as const;

export type JobBuilderStep = (typeof JOB_BUILDER_STEPS)[number]["value"];

/** Said in full where somebody is choosing: when a document is asked for is the decision. */
export const JOB_TIMING_OPTIONS: { value: CollectionTiming; label: string }[] = [
  { value: "on_hire", label: "After the offer is accepted: onboarding paperwork" },
  { value: "on_apply", label: "On the apply form: asked of everyone who applies" },
  { value: "on_offer", label: "When the offer goes out" },
];

/** Each optional stage says WHEN it happens, because that is what has to be predicted. */
export const JOB_STAGE_OPTIONS: { id: AtsInteractiveStage; label: string; detail: string }[] = [
  {
    id: "additional_info",
    label: "Extra information",
    detail: "A form after screening, before review.",
  },
  {
    id: "video",
    label: "Intro video",
    detail: "A short recording after screening, before review.",
  },
  {
    id: "headshot",
    label: "Headshot and bio",
    detail: "Collected once the offer is out, before hire.",
  },
];

/** A stored timing, old words included, as the nearest current answer rather than a blank. */
export const toCollectionTiming = (value: string): CollectionTiming =>
  readCollectionTiming(value).timing;

const STAGES = new Set<string>(ATS_INTERACTIVE_STAGES);
const STATUSES = new Set<string>(JOB_STATUSES);

export function emptyJobValues(facilityId: string): JobValues {
  return {
    facilityId,
    boardId: null,
    title: "",
    slug: null,
    department: null,
    location: null,
    employmentType: null,
    salaryRange: null,
    description: "",
    responsibilities: null,
    requirements: null,
    interactiveStages: [],
    skipOfferLetter: false,
    skipAcceptanceInfo: false,
    acceptanceTemplateId: null,
    allowSignaturesLater: false,
    defaultPayType: null,
    defaultPayRate: null,
    videoInstructions: null,
    status: "draft",
  };
}

/** A stored job as the builder edits it; stages and a status it does not know are dropped. */
export function jobValuesOf(
  job: Omit<JobValues, "interactiveStages" | "status"> & {
    readonly interactiveStages: unknown;
    readonly status: string;
  },
): JobValues {
  const stages: unknown[] = Array.isArray(job.interactiveStages) ? job.interactiveStages : [];
  return {
    facilityId: job.facilityId,
    boardId: job.boardId,
    title: job.title,
    slug: job.slug,
    department: job.department,
    location: job.location,
    employmentType: job.employmentType,
    salaryRange: job.salaryRange,
    description: job.description,
    responsibilities: job.responsibilities,
    requirements: job.requirements,
    interactiveStages: stages.filter(
      (stage): stage is AtsInteractiveStage => typeof stage === "string" && STAGES.has(stage),
    ),
    skipOfferLetter: job.skipOfferLetter,
    skipAcceptanceInfo: job.skipAcceptanceInfo,
    acceptanceTemplateId: job.acceptanceTemplateId,
    allowSignaturesLater: job.allowSignaturesLater,
    defaultPayType: job.defaultPayType,
    defaultPayRate: job.defaultPayRate,
    videoInstructions: job.videoInstructions,
    status: STATUSES.has(job.status) ? (job.status as JobStatus) : "draft",
  };
}

const NULLABLE_FIELDS: ReadonlySet<string> = new Set([
  "boardId",
  "slug",
  "department",
  "location",
  "employmentType",
  "salaryRange",
  "responsibilities",
  "requirements",
  "defaultPayType",
  "defaultPayRate",
  "videoInstructions",
]);

/** A blank optional field clears the column rather than storing an empty string. */
export function clearBlankJobFields<V extends object>(values: V): V {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) =>
      NULLABLE_FIELDS.has(key) && typeof value === "string" && value.trim() === ""
        ? [key, null]
        : [key, value],
    ),
  ) as V;
}

/** The two form fields as a save sends them: the job's own form wins over a template link. */
export function applicationFormFields(draft: {
  readonly jobAppSections: readonly object[];
  readonly jobAppTemplateId: string | null;
}) {
  const own = draft.jobAppSections.length > 0;
  return {
    // The api takes the sections as plain JSON rows; the draft's typed sections are that shape.
    jobAppSections: own ? (draft.jobAppSections as unknown as Record<string, unknown>[]) : null,
    jobAppTemplateId: own ? null : draft.jobAppTemplateId,
  };
}
